import { isLanguageTtsAvailable } from 'api/model';
import { addSpeechUnit, createEmptySubtitles } from './subtitles';
import { PresentationWsClient } from './presentation-ws-client';
import { findActiveRecordingSegment } from './event-state-merge';
import { getMessageType, parseAudioChunk, parsePresenterConfig, parseSpeech, PresentationMessageTypes, } from './presentation-protocol';
import { getDefaultBgColor, getDefaultTextColor, isFontSize } from '../pages/SubtitlesPage/subtitles-settings';
const toLanguageSetup = (language, segment) => {
    if (language === null || segment === null) {
        return { iso: language ?? '', tts: false };
    }
    if (language === segment.audioLanguage) {
        return { iso: language, tts: false };
    }
    const translation = segment.translationSetup.languages.find((item) => item.iso === language);
    return {
        iso: language,
        tts: isLanguageTtsAvailable(segment.translationSetup, language),
    };
};
const isSubtitlesTheme = (value) => value === 'dark' || value === 'light';
const presenterConfigToSettings = (message) => {
    const resolvedTheme = isSubtitlesTheme(message.theme) ? message.theme : 'dark';
    return {
        controls: message.controls ?? 'off',
        theme: resolvedTheme,
        fullscreen: false,
        fontSize: isFontSize(message.fontSize) ? message.fontSize : 'lg',
        textColor: message.textColor ?? getDefaultTextColor(resolvedTheme),
        bgColor: message.bgColor ?? getDefaultBgColor(resolvedTheme),
        textAlign: message.textAlign === 'bottom' ? 'bottom' : 'top',
        customCss: message.customCss ?? '',
    };
};
export class SubtitlesStream {
    eventId;
    presenterId;
    client = null;
    activeSegment = null;
    initLanguage = null;
    currentLanguage = null;
    configPending = false;
    pendingTextToSpeech = false;
    currentTranscript = {
        subtitles: createEmptySubtitles(),
        latency: 'none',
    };
    audioContext = new AudioContext();
    eventHandlers = {};
    nextAudioTime = this.audioContext.currentTime;
    streamAudio = false;
    audioPlaying = false;
    audioSources = new Set();
    constructor(eventId, language = null, presenterId = null) {
        this.eventId = eventId;
        this.initLanguage = language;
        this.presenterId = presenterId;
    }
    isPersistedPresenter() {
        return this.presenterId !== null;
    }
    connect() {
        if (this.client !== null) {
            console.warn('Subtitles stream is already connected. Disconnecting first.');
            this.disconnect();
        }
        this.client = new PresentationWsClient(this.eventId, this.presenterId ?? undefined);
        this.client.connect({
            onPresentationMessage: (message) => {
                this.handleMessage(message.metadataJson, message.binary);
            },
            admin: {
                onEventSnapshot: (snapshot) => {
                    this.handleEventSnapshot(snapshot);
                },
            },
            onOpen: () => {
                console.log('Presentation WebSocket connection established');
                if (!this.isPersistedPresenter()) {
                    this.sendCurrentConfig();
                }
            },
            onClose: () => {
                this.setConfigPending(true);
            },
        });
    }
    setStreamAudio(enabled) {
        if (this.isPersistedPresenter()) {
            return;
        }
        this.pendingTextToSpeech = enabled;
        this.streamAudio = enabled && this.currentLanguage?.tts === true;
        if (this.streamAudio) {
            this.sendCurrentConfig();
            this.audioContext.resume().catch(err => console.error('AudioContext resume failed:', err));
        }
        else {
            this.stopAudio();
            this.sendCurrentConfig();
        }
    }
    disconnect() {
        this.client?.disconnect();
        this.client = null;
        this.currentLanguage = null;
        this.configPending = false;
        this.currentTranscript = {
            subtitles: createEmptySubtitles(),
            latency: 'none',
        };
        this.stopAudio();
        this.nextAudioTime = 0;
        this.activeSegment = null;
    }
    getSelectedLanguage() {
        return this.currentLanguage?.iso ?? null;
    }
    getActiveSegment() {
        return this.activeSegment;
    }
    isConfigPending() {
        return this.configPending;
    }
    isTTSAvailable() {
        return this.currentLanguage?.tts === true;
    }
    getOriginalLanguage() {
        return this.activeSegment?.audioLanguage ?? null;
    }
    selectLanguage(language) {
        if (this.isPersistedPresenter() || this.activeSegment === null) {
            return;
        }
        this.stopAudio();
        this.setConfigPending(true);
        this.client?.sendPresenterConfig(language, this.pendingTextToSpeech && this.isTTSAvailableForLanguage(language));
    }
    on(event, handler) {
        if (this.eventHandlers[event] !== undefined) {
            throw new Error(`Handler for event "${event}" is already registered.`);
        }
        this.eventHandlers[event] = handler;
    }
    isTTSAvailableForLanguage(language) {
        if (this.activeSegment === null) {
            return false;
        }
        if (language === this.activeSegment.audioLanguage) {
            return false;
        }
        return isLanguageTtsAvailable(this.activeSegment.translationSetup, language);
    }
    sendCurrentConfig() {
        this.setConfigPending(true);
        this.client?.sendPresenterConfig(this.currentLanguage?.iso ?? this.initLanguage, this.pendingTextToSpeech && (this.currentLanguage?.tts === true));
    }
    setConfigPending(pending) {
        if (this.configPending === pending) {
            return;
        }
        this.configPending = pending;
        this.eventHandlers.configSync?.(pending);
    }
    handleMessage(metadataJson, binary) {
        const type = getMessageType(metadataJson);
        switch (type) {
            case PresentationMessageTypes.PresenterConfig:
                this.handlePresenterConfig(parsePresenterConfig(metadataJson));
                break;
            case PresentationMessageTypes.Speech:
                this.handleSpeech(parseSpeech(metadataJson, binary));
                break;
            case PresentationMessageTypes.AudioChunk:
                this.handleAudioChunk(parseAudioChunk(metadataJson, binary));
                break;
            default:
                console.warn('Unknown presentation message type:', type);
        }
    }
    handleEventSnapshot(snapshot) {
        const nextSegment = findActiveRecordingSegment(snapshot);
        const prevId = this.activeSegment?.id ?? null;
        const nextId = nextSegment?.id ?? null;
        if (nextId === prevId) {
            if (nextSegment !== null) {
                this.activeSegment = nextSegment;
            }
            return;
        }
        if (nextSegment === null) {
            this.stopAudio();
            this.activeSegment = null;
            this.eventHandlers.segmentEnded?.();
            this.currentTranscript = {
                subtitles: createEmptySubtitles(),
                latency: 'none',
            };
            return;
        }
        this.activeSegment = nextSegment;
        this.eventHandlers.segmentStarted?.(nextSegment, toLanguageSetup(this.currentLanguage?.iso ?? this.initLanguage, nextSegment));
    }
    handlePresenterConfig(message) {
        const languageSetup = toLanguageSetup(message.language, this.activeSegment);
        this.currentLanguage = languageSetup;
        this.streamAudio = message.textToSpeech && languageSetup.tts;
        this.setConfigPending(false);
        this.eventHandlers.languageChanged?.(languageSetup);
        if (this.isPersistedPresenter()) {
            this.eventHandlers.presenterSettingsChanged?.(presenterConfigToSettings(message));
        }
    }
    handleSpeech(message) {
        if (this.activeSegment === null) {
            return;
        }
        const unit = {
            finals: message.payload.finals.map((fragment) => ({
                text: fragment.text,
                speaker: fragment.speaker ?? '',
            })),
            drafts: message.payload.drafts.map((fragment) => ({
                text: fragment.text,
                speaker: fragment.speaker ?? '',
            })),
        };
        const finalText = unit.finals.map(f => f.text).join(' / ');
        const draftText = unit.drafts.map(d => d.text).join(' / ');
        console.log(`Final: "${finalText}" Draft: "${draftText}"`);
        const prevSubtitles = this.currentTranscript.subtitles;
        this.currentTranscript = {
            subtitles: addSpeechUnit(prevSubtitles, unit),
            latency: 'none',
        };
        this.eventHandlers.transcriptUpdated?.(this.currentTranscript);
    }
    handleAudioChunk(message) {
        if (!this.streamAudio) {
            return;
        }
        if (this.activeSegment === null) {
            return;
        }
        if (message.language !== this.currentLanguage?.iso) {
            return;
        }
        if (this.audioPlaying === false) {
            this.eventHandlers.voiceStarted?.();
            this.audioPlaying = true;
        }
        if (this.audioContext.state !== 'running') {
            this.audioContext.resume().catch(err => console.error('AudioContext resume failed:', err));
        }
        const pcm16 = new Int16Array(message.bytes.slice().buffer);
        const float32 = new Float32Array(pcm16.length);
        for (let i = 0; i < pcm16.length; i++) {
            float32[i] = pcm16[i] / 32768;
        }
        const buffer = this.audioContext.createBuffer(1, float32.length, 16000);
        buffer.copyToChannel(float32, 0);
        const audioSource = this.audioContext.createBufferSource();
        audioSource.buffer = buffer;
        audioSource.connect(this.audioContext.destination);
        audioSource.onended = () => {
            this.audioSources.delete(audioSource);
            this.audioPlaying = false;
        };
        this.audioSources.add(audioSource);
        if (this.nextAudioTime < this.audioContext.currentTime) {
            this.nextAudioTime = this.audioContext.currentTime;
        }
        audioSource.start(this.nextAudioTime);
        this.nextAudioTime += buffer.duration;
    }
    stopAudio() {
        this.audioSources.forEach(source => {
            try {
                source.stop();
            }
            catch (e) {
                // Ignore errors if source already stopped
            }
        });
        this.audioSources.clear();
        this.audioPlaying = false;
    }
}
